import { useMemo } from "react";

import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import { customRequest } from "app/modules/api-client";
import { buildCoreAverageSeries } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/shared/coreCompetitorsUtils";
import { MAX_SELECTED_BRANDS } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/model-comparison/consts";
import type {
    IModelComparisonOvertimeResponse,
    ITopicOvertimeData,
    SeriesData,
    SeriesItem,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/model-comparison/types";
import { EViewMode } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/model-comparison/types";
import { useLLMReportQuery } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMReportQuery";
import {
    assignDynamicLegendColors,
    sortByMyBrandFirst,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    IAiTrackerCampaign,
    ILLMWidgetRequestParams,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

interface UseModelComparisonOvertimeDataResult {
    data: IModelComparisonOvertimeResponse | undefined;
    transformedData: Record<EViewMode.VisibilityOvertime, SeriesData> | undefined;
    isLoading: boolean;
    isError: boolean;
}

const fetchCompareOvertime = async (params: ILLMWidgetRequestParams): Promise<IModelComparisonOvertimeResponse> =>
    customRequest<IModelComparisonOvertimeResponse>({
        path: `/api/aiTracker/reports/brandVisibilityCompareReport/BrandMetricsGraphCompareOvertime`,
        method: "GET",
        query: params,
        format: "json",
    });

const convertDateKeysToTimestamps = (data: Record<string, number> | undefined): number[][] => {
    if (!data || Object.keys(data).length === 0) {
        return [];
    }

    return Object.entries(data)
        .map(([dateStr, value]) => [dayjs.utc(dateStr).valueOf(), value])
        .sort((a, b) => a[0] - b[0]);
};

const transformOvertimeTopicData = (
    overtimeData: ITopicOvertimeData,
    myBrandDisplayName: string,
    granularity: GranularityNames,
    topicKey: string,
    onlyCoreCompetitors: boolean,
): SeriesItem[] => {
    const results: Array<SeriesItem & { isMyBrand?: boolean }> = [];

    if (overtimeData.myBrand) {
        const data = granularity === GranularityNames.DAILY ? overtimeData.myBrand.daily : overtimeData.myBrand.weekly;
        results.push({
            id: `${EViewMode.VisibilityOvertime}-${topicKey}-${myBrandDisplayName}`,
            name: myBrandDisplayName,
            color: "",
            total: overtimeData.myBrand.total ?? 0,
            data: convertDateKeysToTimestamps(data),
            visible: true,
            isMyBrand: true,
        });
    }

    overtimeData.brands?.forEach((brand) => {
        if (onlyCoreCompetitors && !brand.isCoreCompetitor) {
            return;
        }
        const data =
            granularity === GranularityNames.DAILY ? brand.visibilityData?.daily : brand.visibilityData?.weekly;
        results.push({
            id: `${EViewMode.VisibilityOvertime}-${topicKey}-${brand.brandName}`,
            name: brand.brandName,
            color: "",
            total: brand.visibilityData?.total ?? 0,
            data: convertDateKeysToTimestamps(data),
            visible: true,
            isMyBrand: false,
        });
    });

    return sortByMyBrandFirst(results).map((data, index) => ({ ...data, visible: index < MAX_SELECTED_BRANDS }));
};

const transformOvertimeResponse = (
    campaign: IAiTrackerCampaign,
    overtimeResponse: IModelComparisonOvertimeResponse,
    granularity: GranularityNames,
    onlyCoreCompetitors: boolean,
): SeriesData => {
    const isMultiBrand = campaign.brands.length > 1;
    const myBrandDisplayName = translate(
        isMultiBrand ? "llm.llm-comparison.graph.legend.my_brand" : "llm.llm-comparison.graph.legend.my_brand.single",
        { brand: campaign.brands[0] },
    );

    if (!overtimeResponse?.topics) {
        return {};
    }

    return Object.entries(overtimeResponse.topics).reduce<SeriesData>((acc, [topicName, topicData]) => {
        const coloredBrands = assignDynamicLegendColors(
            transformOvertimeTopicData(
                topicData,
                myBrandDisplayName,
                granularity,
                topicName,
                onlyCoreCompetitors,
            ),
        );

        const coreAverageSeries = onlyCoreCompetitors
            ? buildCoreAverageSeries(topicName, topicData.coreAverage, granularity)
            : null;
        acc[topicName] = coreAverageSeries ? [...coloredBrands, coreAverageSeries] : coloredBrands;
        
        return acc;
    }, {});
};

export const useModelComparisonOvertimeData = (
    params: ILLMWidgetRequestParams,
    campaign: IAiTrackerCampaign | undefined,
    granularity: GranularityNames,
    onlyCoreCompetitors: boolean,
    options?: { enabled?: boolean },
): UseModelComparisonOvertimeDataResult => {
    const { data, isLoading, isError } = useLLMReportQuery<IModelComparisonOvertimeResponse, ILLMWidgetRequestParams>(
        "modelComparisonOvertime",
        fetchCompareOvertime,
        params,
        options,
    );

    const transformedData = useMemo<Record<EViewMode.VisibilityOvertime, SeriesData> | undefined>(() => {
        if (!data?.topics || !campaign) {
            return undefined;
        }

        try {
            return {
                [EViewMode.VisibilityOvertime]: transformOvertimeResponse(
                    campaign,
                    data,
                    granularity,
                    onlyCoreCompetitors,
                ),
            };
        } catch (error: unknown) {
            Sentry.errorTracker({
                e: error,
                message: "Error transforming LLM comparison overtime data",
                level: "error",
                params: { extra: { moduleName: "llm-tracker", campaignId: campaign?.id } },
            });
            return undefined;
        }
    }, [data, campaign, granularity, onlyCoreCompetitors]);

    return { data, transformedData, isLoading, isError };
};
